import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  Gavel as GavelIcon,
  Home as HomeIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';
import { anonymizeState, anonymizeDistrict } from '../../utils/anonymize';

interface CaseSummaryData {
  total_facilities: number;
  total_billed: number;
  total_paid: number;
  states: { state: string; count: number }[];
  districts: { district_name: string; district_abbrev: string; count: number }[];
  statute_of_limitations: string;
  earliest_scheme_date: string;
  facilities_still_owned: number;
  total_case_facilities: number;
}

interface CaseSummaryProps {
  caseId: string;
  demoMode?: boolean;
}

export const CaseSummary: React.FC<CaseSummaryProps> = ({ caseId, demoMode = false }) => {
  const { access } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  const [summary, setSummary] = useState<CaseSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSummary();
  }, [caseId, access]);

  const fetchSummary = async () => {
    if (!caseId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/cases/${caseId}/summary/`, { headers });
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch case summary:', err);
      setError('Failed to load case summary');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !summary) {
    return null;
  }

  const ownershipPercentage = summary.total_facilities > 0
    ? ((summary.facilities_still_owned / summary.total_facilities) * 100).toFixed(1)
    : '0.0';

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Case Summary
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Facilities & Financial Overview */}
        <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Facilities & Financials
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Tooltip
                    title={
                      summary.total_case_facilities > summary.total_facilities
                        ? `${summary.total_case_facilities - summary.total_facilities} facilities were removed because defendant did not own the facility during any scheme dates`
                        : ''
                    }
                    arrow
                    placement="top"
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Facilities
                        </Typography>
                        {summary.total_case_facilities > summary.total_facilities && (
                          <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {summary.total_facilities}
                        </Typography>
                        {summary.total_case_facilities > summary.total_facilities && (
                          <Typography variant="h6" color="text.secondary">
                            / {summary.total_case_facilities}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Tooltip>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Billed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatMoney(summary.total_billed)}
                  </Typography>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Paid
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatMoney(summary.total_paid)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>

        {/* Geographic Distribution */}
        <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HomeIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Geographic Distribution
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    States ({summary.states.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {summary.states.map((state) => {
                      const displayState = demoMode ? anonymizeState(state.state) : state.state;
                      return (
                        <Chip
                          key={state.state}
                          label={`${displayState} (${state.count})`}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box sx={{ flex: 1 }}>
                  {(() => {
                    // In demo mode, deduplicate districts after anonymization
                    const displayDistricts = demoMode
                      ? Array.from(new Set(summary.districts.map((district) =>
                          anonymizeDistrict(district.district_abbrev, anonymizeState(district.district_abbrev.split('.')[1]?.trim() || ''))
                        )))
                      : summary.districts.map(d => d.district_abbrev);

                    return (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Federal Districts ({displayDistricts.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {demoMode
                            ? displayDistricts.map((district) => (
                                <Chip
                                  key={district}
                                  label={district}
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                              ))
                            : summary.districts.map((district) => (
                                <Chip
                                  key={district.district_abbrev}
                                  label={`${district.district_abbrev} (${district.count})`}
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                              ))
                          }
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              </Box>
            </CardContent>
        </Card>

        {/* Legal Timeline */}
        <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GavelIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Legal Timeline
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Earliest Scheme Date
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {new Date(summary.earliest_scheme_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Statute of Limitations <Typography component="span" variant="caption" color="text.secondary">(+6 years)</Typography>
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {new Date(summary.statute_of_limitations).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>

        {/* Current Ownership */}
        <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Current Ownership
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Facilities Still Owned by Defendant <Typography component="span" variant="caption" color="text.secondary">(June 2025)</Typography>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {summary.facilities_still_owned}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    / {summary.total_facilities}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({ownershipPercentage}%)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
        </Card>
      </Box>
    </Box>
  );
};